import { createReducer, isAnyOf, UnknownAction } from '@reduxjs/toolkit'
import { DocumentType } from '@repo/shared/dtos/document'
import { TimeType } from '@repo/shared/dtos/generic'
import { isNoteDocument } from '@repo/shared/utils'
import { AsyncData, DataStatus } from '@repo/ui/utils'

import { storeActions } from '@/store/actions'

export type Note = {
  id: string
  content: string
  createdAt: TimeType
  type:
    | DocumentType.NOTE
    | DocumentType.NOTE_FROM_EMAIL_ANALYSIS
    | DocumentType.NOTE_FROM_FILE_ANALYSIS
  updatedAt: TimeType
}

export type DocumentFile = {
  createdAt: TimeType
  fileName: string
  id: string
  size: number
  title: string
  type:
    | DocumentType.PDF
    | DocumentType.IMAGE
    | DocumentType.MICROSOFT_DOCUMENT
    | DocumentType.CSV
    | DocumentType.TEXT
  updatedAt: TimeType
}

export type Document = Note | DocumentFile

export type DocumentState = {
  all: AsyncData<Document[]>
}

export const documentInitialState: DocumentState = {
  all: DataStatus.Uninitialized,
}

export const documentReducer: ((
  state: DocumentState | undefined,
  action: UnknownAction,
) => DocumentState) & {
  getInitialState: () => DocumentState
} = createReducer(documentInitialState, (builder) => {
  builder.addCase(storeActions.cleanUpOrganization, () => {
    return documentInitialState
  })

  builder
    .addCase(
      storeActions.beneficiary.initBeneficiary.pending,
      (
        state,
        {
          meta: {
            arg: { isSameBeneficiary },
          },
        },
      ) => {
        if (isSameBeneficiary) return
        state.all = DataStatus.Loading
      },
    )
    .addCase(storeActions.beneficiary.initBeneficiary.rejected, (state) => {
      state.all = DataStatus.Error
    })
    .addCase(
      storeActions.beneficiary.initBeneficiary.fulfilled,
      (state, { payload: { documents } }) => {
        state.all = documents
      },
    )

  builder.addCase(storeActions.initDashboard, (state) => {
    state.all = documentInitialState.all
  })

  builder.addCase(
    storeActions.beneficiary.document.delete.fulfilled,
    (
      state,
      {
        meta: {
          arg: { document },
        },
      },
    ) => {
      if (DataStatus.isFulfilled(state.all))
        state.all = state.all.filter((doc) => doc.id !== document.id)
    },
  )

  builder.addCase(
    storeActions.beneficiary.document.update.fulfilled,
    (state, { payload: { document } }) => {
      const isNote = isNoteDocument(document)
      if (DataStatus.isFulfilled(state.all))
        state.all = state.all.map((n) =>
          n.id === document.id
            ? isNote
              ? { ...n, content: document.content }
              : { ...n, title: document.title }
            : n,
        )
      else state.all = [document]
    },
  )

  builder.addMatcher(
    isAnyOf(
      storeActions.beneficiary.document.create.fulfilled,
      storeActions.beneficiary.document.restore.fulfilled,
    ),
    (state, { payload: { document } }) => {
      if (DataStatus.isFulfilled(state.all)) {
        const existingDocument = state.all.find((n) => n.id === document.id)
        if (!existingDocument) state.all.push(document)
      } else state.all = [document]
    },
  )
})
